import { 
  Button, 
  Card, 
  CardHeader, 
  Drawer, 
  DrawerBody, 
  DrawerHeader, 
  DrawerHeaderTitle, 
  Title3,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Text,
  Subtitle2,
  Spinner,
  Dropdown,
  CardPreview,
  Tooltip
} from "@fluentui/react-components";
import { BinRecycle20Regular, DeleteRegular, DismissRegular, Edit12Regular, Edit16Regular, EditRegular, EyeOffRegular, EyeRegular, KeyRegular, PersonAddRegular, PersonProhibitedRegular, RecycleRegular, SearchRegular, ShieldRegular } from "@fluentui/react-icons";
import { User, UserUpdateDTO } from "../../models/users";
import { Fragment, useEffect, useState } from "react";
import { getUserById, getUsers, getUserWithRolesAndModules, updateUser } from "../../services/usersService";
import { deleteUserRoleAssignment, removeGlobalKontoadmin } from "../../services/roleService";
import EditUserDialog from "../dialogs/EditUserDialog";
import { useAppContext } from "../../AppContext";
import { Organization } from "../../models/organizations";
import AddRolesDialog from "../dialogs/AddRolesDialog";
import ChangePasswordDialog from "../dialogs/ChangePasswordDialog";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import { set } from "react-hook-form";
import { UserGroup } from "../../models/userGroup";
import { getUserGroupsByUser } from "../../services/userGroupService";
import { Module } from "../../models/module";
import { RoleDTO } from "../../models/role";

type Props = {
  onClose: () => void;
  open: boolean;
  user: User;
  organizations: Organization[];
  onUserUpdated: (updatedUser: User) => void;
};

const UserDrawer = (props: Props) => {
  const [user, setUser] = useState<any>();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAddRolesDialogOpen, setIsAddRolesDialogOpen] = useState(false);
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isConfirmInactivateOpen, setIsConfirmInactivateOpen] = useState(false);
  const [isConfirmReactivateOpen, setIsConfirmReactivateOpen] = useState(false);
  const [reactivateError, setReactivateError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ roleID: number; moduleID: number; userGroupID?: number } | null>(null);
  const [userOrganization, setUserOrganization] = useState<Organization>();
  const [procurerUser, setProcurerUser] = useState<boolean>(false);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [selectedEditAssignment, setSelectedEditAssignment] = useState<any | null>(null);

  const {user: loggedUser, featurePermissions} = useAppContext();

  // A user who is Kontoadmin on many ramavtal (a "global" Kontoadmin) gets one summary row instead of one per ramavtal.
  // Only sysadmin sees it collapsed, since only sysadmin can remove them all at once.
  const GLOBAL_KONTOADMIN_MIN_MODULES = 5;
  const [isConfirmRemoveGlobalOpen, setIsConfirmRemoveGlobalOpen] = useState(false);
  const isSysadmin = loggedUser?.roles?.some((r: RoleDTO) => r.roleType === 'General') ?? false;
  const kontoadminAssignments: any[] = user?.assignments?.filter((a: any) => a.role.roleName === 'Kontoadmin') ?? [];
  const collapseKontoadmin = isSysadmin && kontoadminAssignments.length >= GLOBAL_KONTOADMIN_MIN_MODULES;

  const handleRemoveGlobalKontoadmin = async () => {
    try {
      await removeGlobalKontoadmin(props.user?.userID);
      const userData = await getUserWithRolesAndModules(props.user?.userID);
      setUser(userData);
    } catch (error) {
      console.error('Error removing global Kontoadmin:', error);
    }
  };

  useEffect(() => {
    if (props.open) {
      const fetchUser = async () => {
        try {
          setLoading(true);
          const userData = await getUserWithRolesAndModules(props.user?.userID);
          setProcurerUser(userData.user.organizationID > 0);
          setUser(userData);
        } catch (error) {
          console.error('Error fetching users:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchUser();
    }
  }, [props.open, props.user]);

  useEffect(() => {
    const fetchUserGroups = async () => {
      if(procurerUser && user){
        setUserOrganization(props.organizations.find(org => org.organizationID === user.user.organizationID));
        try{
          const userGroups = await getUserGroupsByUser(props.user.userID);
          setUserGroups(userGroups);
        }
        catch(error){
          console.error('Error fetching user groups:', error);
        }
      }
    };
    fetchUserGroups();
  }, [procurerUser, user]);

  const handleUserUpdated = (updatedUser: User) => {
    setUser((prev: any) => ({ ...prev, user: updatedUser }));
    props.onUserUpdated(updatedUser);
  };

  const handleRoleAssigned = async () => {
    // Refetch user data to update the assignments table
    try {
      const userData = await getUserWithRolesAndModules(props.user?.userID);
      setUser(userData);
    } catch (error) {
      console.error('Error refetching user data:', error);
    }
  };

  const handleDeleteAssignment = async (roleID: number, moduleID: number, userGroupID?: number) => {
    try {
      await deleteUserRoleAssignment(props.user?.userID, roleID, moduleID, userGroupID);
      // Refetch user data to update the table
      const userData = await getUserWithRolesAndModules(props.user?.userID);
      setUser(userData);
    } catch (error) {
      console.error('Error deleting assignment:', error);
    }
  };

  const handleInactivateUser = async () => {
    try {
      const disabledDate = "-disabled" + new Date().toISOString().split("T")[0]
      const updatedUser: UserUpdateDTO = {
        Email: user.user.email + disabledDate,
        FirstName: user.user.firstName,
        LastName: user.user.lastName,
        IsDisabled: true
      };
      await updateUser(user.user.userID, updatedUser);
      props.onUserUpdated({ ...user.user, isDisabled: true });
      props.onClose();
    } catch (error) {
      console.error('Error inactivating user:', error);
    }
  };

  const handleReactivateUser = async () => {
    try {
      setReactivateError(null);
      // Deactivation appends "-disabledYYYY-MM-DD" to the email; the API restores the original one
      const updatedUser: UserUpdateDTO = {
        Email: user.user.email,
        FirstName: user.user.firstName,
        LastName: user.user.lastName,
        IsDisabled: false
      };
      await updateUser(user.user.userID, updatedUser);
      props.onUserUpdated({ ...user.user, isDisabled: false });
      props.onClose();
    } catch (error: any) {
      console.error('Error reactivating user:', error);
      setReactivateError(error?.response?.data?.message ?? 'Kontot kunde inte aktiveras.');
    }
  };

  const handlePasswordUpdate = async (password: string) => {
    try {
      const updatedUser: UserUpdateDTO = {
        Email: user.user.email,
        FirstName: user.user.firstName,
        LastName: user.user.lastName, 
        Title: user.user.title, 
        PhoneNumber: user.user.phoneNumber,
        Password: password
      };
      const response = await updateUser(user.user.userID, updatedUser);
    }catch(error){
      console.error('Error deleting password:', error);
    }
  }

  const getParentModuleName = (modulePath: string) => {
    const parts = modulePath.split('_');
    const module = loggedUser?.modules?.find((m: Module) => m.moduleID === parseInt(parts[2]));

    return module?.moduleName ?? '';
  }

  const userGroupRoleNames = ["Kommunanvändare", "Behörighetsansvarig"];

  const getCandidateUserGroupsForAssignment = (assignment: any): any[] => {
    if (!procurerUser || !userGroupRoleNames.includes(assignment.role.roleName)) return [];
    return userGroups.filter((g: any) =>
      g.moduleID === assignment.module.moduleID &&
      g.userGroupAssignments?.some((uga: any) =>
        uga.user?.userID === props.user.userID &&
        uga.roles?.some((r: any) => r.roleID === assignment.role.roleID)
      )
    );
  };

  // A user can have the exact same role (e.g. "Kommunanvändare") in more than
  // one group for the same module, so a role/module match alone is ambiguous.
  // Resolve every assignment in one pass, removing a (group, roleID) pair from
  // the candidate pool once it's been claimed by an earlier assignment - a
  // single group can still be reused for a *different* role it also grants,
  // so the group alone must not be consumed, only that specific role in it.
  const getAssignmentUserGroups = (assignments: any[]): any[] => {
    const consumedGroupRolePairs = new Set<string>();
    return assignments.map((assignment: any) => {
      const candidates = getCandidateUserGroupsForAssignment(assignment);
      const group = candidates.find((g: any) => !consumedGroupRolePairs.has(`${g.userGroupID}-${assignment.role.roleID}`))
        ?? candidates[0]
        ?? null;
      if (group) consumedGroupRolePairs.add(`${group.userGroupID}-${assignment.role.roleID}`);
      return group;
    });
  };

  return (
  <>
    <Drawer
      position="end"
      size="medium"
      open={props.open}
      onOpenChange={(_event, data) => !data.open && props.onClose()}
      >
      <DrawerHeader>
        <DrawerHeaderTitle>
          <div className="flex items-start justify-between w-full">
            <h2>
              {user?.user?.firstName} {user?.user?.lastName}
            </h2>
            <Button size="small" icon={<DismissRegular />} shape="circular" onClick={props.onClose}>
            </Button>
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Spinner size="large" label="Laddar användarinformation..." />
          </div>
        ) : user ? (
          <>
        <div className="flex gap-2 mt-5">
          {!user.user.isDisabled && featurePermissions?.some(fp => fp.action == "Deactivate user account") && (
            <Button onClick={() => setIsConfirmInactivateOpen(true)} icon={<EyeOffRegular />}>
              Inaktivera konto
            </Button>
          )}
          {user.user.isDisabled && featurePermissions?.some(fp => fp.action == "Reactivate user") && (
            <Button onClick={() => setIsConfirmReactivateOpen(true)} icon={<EyeRegular />} appearance="primary">
              Aktivera konto
            </Button>
          )}
          {featurePermissions?.some(fp => fp.action == "Change user password") && (
            <Button onClick={() => setIsChangePasswordDialogOpen(true)} icon={<KeyRegular />}>
              Byt lösenord
            </Button>
          )}
        </div>
        {user.user.isDisabled && (
          <Text className="mt-2" size={200}>Kontot är inaktivt.</Text>
        )}
        {reactivateError && (
          <Text className="mt-2" size={200} style={{ color: 'var(--colorPaletteRedForeground1)' }}>{reactivateError}</Text>
        )}
        <Card className="mt-5">
          <CardPreview>
            <Title3>Användare</Title3>
          </CardPreview>
          <div className="flex flex-col gap-1 mb-3">
            <Text size={400} weight="semibold">
              E-post:  <Text size={400}>{user.user.email}</Text>
            </Text>
            <Text size={400} weight="semibold">
              Förnamn:  <Text size={400}>{user.user.firstName}</Text>
            </Text>
            <Text size={400} weight="semibold">
              Efternamn:  <Text size={400}>{user.user.lastName}</Text>
            </Text>
            <Text size={400} weight="semibold">
              Titel:  <Text size={400}>{user.user.title}</Text>
            </Text>
            <Text size={400} weight="semibold">
              Telefon:  <Text size={400}>{user.user.phoneNumber}</Text>
            </Text>
          </div>
          <div className="flex gap-2"> 
            <Button onClick={() => setIsEditDialogOpen(true)} icon={<EditRegular />}>
              Ändra uppgifter
            </Button>
          </div>
        </Card>
        {featurePermissions?.some(p => p.action == 'See user log') && <Card className="mt-5">
          <CardPreview>
            <Title3>Användarlogg</Title3>
          </CardPreview>
          <div className="flex flex-col gap-1 mb-3">
            <Text size={400} weight="semibold">
              Konto skapat:  <Text size={400}>{new Date(user.user.created).toLocaleString('sv-SE')}</Text>
            </Text>
            <Text size={400} weight="semibold">
              Senast inloggning:  <Text size={400}>{new Date(user.user.lastActivity).toLocaleString('sv-SE')}</Text>
            </Text>
            <Text size={400} weight="semibold">
              Felaktiga lösenordförsök:  <Text size={400}>{user.user.failedLogins}</Text>
            </Text>
            <Text size={400} weight="semibold">
              Ändring av lösenord:  <Text size={400}>{user.user.passwordChangeCount}</Text>
            </Text>
          </div>
        </Card>}
        {featurePermissions?.some(p => p.action == 'Assign user group') && <Card className="mt-5">
          <CardPreview>
            <Title3>Behörigheter</Title3>
          </CardPreview>
          <div className="flex flex-col gap-3 mb-3">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>
                  <Text weight="semibold">
                    {user.user.organizationID > 0 ? "Organisation" : "Ramavtal"}
                  </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                  <Text weight="semibold">
                    {user.user.organizationID > 0 ? "Ramavtal" : "Avdelning"}
                  </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                  <Text weight="semibold">
                    Behörighet
                  </Text>
                  </TableHeaderCell>
                  <TableHeaderCell style={{width: '100px'}}>
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {collapseKontoadmin && (
                  <TableRow className="group">
                    <TableCell>
                      <Text>{userOrganization?.organizationName}</Text>
                    </TableCell>
                    <TableCell>
                      <Tooltip content={kontoadminAssignments.map((a: any) => a.module.moduleName).join(', ')} relationship="description">
                        <Text>Alla ramavtal ({kontoadminAssignments.length})</Text>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Text>Kontoadmin</Text>
                    </TableCell>
                    <TableCell style={{width: '100px'}}>
                      <div className="flex gap-1">
                        <Tooltip content="Radera Kontoadmin i alla ramavtal" relationship="label">
                          <Button
                            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            shape="circular"
                            icon={<BinRecycle20Regular />}
                            onClick={() => setIsConfirmRemoveGlobalOpen(true)}
                          />
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {user.assignments && user.assignments.length > 0 && (() => {
                  const assignmentUserGroups = getAssignmentUserGroups(user.assignments);
                  return user.assignments.map((assignment: any, index: number) => {
                    const assignmentUserGroup = assignmentUserGroups[index];
                    if (collapseKontoadmin && assignment.role.roleName === 'Kontoadmin') return null;
                    return (
                    <Fragment key={index}>
                    <TableRow className="group">
                      <TableCell>
                        {user.user.organizationID > 0 ? <Text>{procurerUser ? userOrganization?.organizationName : props.organizations.find(org => org.organizationID === assignment.module.organizationID)?.organizationName}</Text> : <Text>{getParentModuleName(assignment.module.modulePath)}</Text>}
                      </TableCell>
                      <TableCell>
                        <Text>{assignment.module.moduleName}</Text>
                      </TableCell>
                      <TableCell>
                        <Text>{assignment.role.roleName}</Text>
                      </TableCell>
                      <TableCell style={{width: '100px'}}>
                        <div className="flex gap-1">
                          <Tooltip content="Redigera behörighet" relationship="label">
                            <Button
                              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              shape="circular"
                              icon={<EditRegular />}
                              onClick={() => { setSelectedEditAssignment(assignment); setIsAddRolesDialogOpen(true); }}
                            />
                          </Tooltip>
                          <Tooltip content="Radera behörighet" relationship="label">
                            <Button
                              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              shape="circular"
                              icon={<BinRecycle20Regular />}
                              onClick={() => {
                                setPendingDelete({ roleID: assignment.role.roleID, moduleID: assignment.module.moduleID, userGroupID: assignmentUserGroup?.userGroupID });
                                setIsConfirmDeleteOpen(true);
                              }}
                            />
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                    {assignmentUserGroup && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Text italic size={200}>
                            Grupp: {assignmentUserGroup.userGroupName}
                          </Text>
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                    );
                  });
                })()}
              </TableBody>
            </Table>
            <Button onClick={() => { setSelectedEditAssignment(null); setIsAddRolesDialogOpen(true); }} className="w-2/5" icon={<ShieldRegular />}>Lägg till behörigheter</Button>
          </div>
        </Card>}
        </>
        ) : null}
      </DrawerBody>
    </Drawer>
    {user && (
      <>
        {isEditDialogOpen && (
          <EditUserDialog
            user={user.user}
            open={isEditDialogOpen}
            onClose={() => setIsEditDialogOpen(false)}
            onUserUpdated={handleUserUpdated} 
          />
        )}
        {isAddRolesDialogOpen && (
          <AddRolesDialog
            open={isAddRolesDialogOpen}
            onClose={() => { setIsAddRolesDialogOpen(false); setSelectedEditAssignment(null); }}
            organizations={props.organizations}
            user={user.user}
            onRoleAssigned={handleRoleAssigned}
            initialAssignment={selectedEditAssignment}
          />
        )}
        {isChangePasswordDialogOpen && (
          <ChangePasswordDialog
            open={isChangePasswordDialogOpen}
            onClose={() => setIsChangePasswordDialogOpen(false)}
            userID={user.user.userID}
            onPasswordChanged={(password) => handlePasswordUpdate(password)}
          />
        )}
      </>
    )}
    <ConfirmationDialog
      isOpen={isConfirmDeleteOpen}
      onClose={() => { setIsConfirmDeleteOpen(false); setPendingDelete(null); }}
      onConfirm={() => {
        if (pendingDelete) handleDeleteAssignment(pendingDelete.roleID, pendingDelete.moduleID, pendingDelete.userGroupID);
        setIsConfirmDeleteOpen(false);
        setPendingDelete(null);
      }}
      title="Radera behörighet"
      text="Är du säker på att du vill radera denna behörighet?"
    />
    <ConfirmationDialog
      isOpen={isConfirmRemoveGlobalOpen}
      onClose={() => setIsConfirmRemoveGlobalOpen(false)}
      onConfirm={() => {
        handleRemoveGlobalKontoadmin();
        setIsConfirmRemoveGlobalOpen(false);
      }}
      title="Radera Kontoadmin i alla ramavtal"
      text={`Är du säker på att du vill ta bort Kontoadmin i alla ${kontoadminAssignments.length} ramavtal för ${user?.user?.firstName} ${user?.user?.lastName}?`}
    />
    <ConfirmationDialog
      isOpen={isConfirmInactivateOpen}
      onClose={() => setIsConfirmInactivateOpen(false)}
      onConfirm={() => {
        handleInactivateUser();
        setIsConfirmInactivateOpen(false);
      }}
      title="Inaktivera konto"
      text={`Är du säker på att du vill inaktivera kontot för ${user?.user?.firstName} ${user?.user?.lastName}?`}
    />
    <ConfirmationDialog
      isOpen={isConfirmReactivateOpen}
      onClose={() => setIsConfirmReactivateOpen(false)}
      onConfirm={() => {
        handleReactivateUser();
        setIsConfirmReactivateOpen(false);
      }}
      title="Aktivera konto"
      text={`Är du säker på att du vill aktivera kontot för ${user?.user?.firstName} ${user?.user?.lastName}?`}
    />
    </>
  );
};

export default UserDrawer;